import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Statsig } from "statsig-react";
import type { StatsigUser } from "statsig-js";
import { BusinessState } from "redux-context/business";
import { UserState } from "redux-context/user";
import {
  PAY_BEFORE_SETUP_CAMPAIGN_GATE,
  STATSIG_CLIENT_KEY,
  STATSIG_ENV_TIER,
} from "./constants";
import {
  normalizeStatsigEnvironmentTier,
  resolveStatsigGateValue,
} from "./statsigHelpers";

type FeatureFlagsContextValue = {
  payBeforeSetupCampaignGateEnabled: boolean;
};

const DEFAULT_FEATURE_FLAGS: FeatureFlagsContextValue = {
  payBeforeSetupCampaignGateEnabled: false,
};

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>(
  DEFAULT_FEATURE_FLAGS,
);

const buildStatsigUser = (
  userId: string,
  email?: string,
  defaultBusinessId?: string,
  currentBusinessId?: string,
): StatsigUser => {
  const custom: Record<string, string> = {};
  if (defaultBusinessId) {
    custom.default_business_id = defaultBusinessId;
  }
  if (currentBusinessId) {
    custom.current_business_id = currentBusinessId;
  }

  return {
    userID: userId,
    email,
    custom: Object.keys(custom).length > 0 ? custom : undefined,
  };
};

export const FeatureFlagsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { loggedUser } = useSelector((state: UserState) => state.user);
  const { business } = useSelector((state: BusinessState) => state.business);
  const [payBeforeSetupCampaignGateEnabled, setPayBeforeSetupCampaignGateEnabled] =
    useState(false);

  const userId = loggedUser?.id;
  const email = loggedUser?.email;
  const defaultBusinessId = loggedUser?.defaultBusinessId;
  const currentBusinessId = business?._id;

  useEffect(() => {
    let isMounted = true;

    const initializeStatsig = async () => {
      if (!STATSIG_CLIENT_KEY || !userId) {
        if (Statsig.initializeCalled()) {
          Statsig.shutdown();
        }
        if (isMounted) {
          setPayBeforeSetupCampaignGateEnabled(false);
        }
        return;
      }

      const statsigUser = buildStatsigUser(
        userId,
        email,
        defaultBusinessId,
        currentBusinessId,
      );
      const statsigTier = normalizeStatsigEnvironmentTier(STATSIG_ENV_TIER);
      const statsigOptions = statsigTier
        ? { environment: { tier: statsigTier } }
        : undefined;

      try {
        if (Statsig.initializeCalled()) {
          await Statsig.updateUser(statsigUser);
        } else {
          await Statsig.initialize(STATSIG_CLIENT_KEY, statsigUser, statsigOptions);
        }

        if (!isMounted) {
          return;
        }

        setPayBeforeSetupCampaignGateEnabled(
          resolveStatsigGateValue({
            clientKey: STATSIG_CLIENT_KEY,
            userId,
            isInitialized: true,
            readGate: () => Statsig.checkGate(PAY_BEFORE_SETUP_CAMPAIGN_GATE),
          }),
        );
      } catch (_error) {
        if (isMounted) {
          setPayBeforeSetupCampaignGateEnabled(false);
        }
      }
    };

    initializeStatsig();

    return () => {
      isMounted = false;
    };
  }, [currentBusinessId, defaultBusinessId, email, userId]);

  useEffect(() => {
    return () => {
      if (Statsig.initializeCalled()) {
        Statsig.shutdown();
      }
    };
  }, []);

  const contextValue = useMemo<FeatureFlagsContextValue>(() => {
    return {
      payBeforeSetupCampaignGateEnabled,
    };
  }, [payBeforeSetupCampaignGateEnabled]);

  return (
    <FeatureFlagsContext.Provider value={contextValue}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlagsContextValue => {
  return useContext(FeatureFlagsContext);
};
